import i18n from "i18next";
import { useEffect, useState } from "react";
import type { Document } from "@/lib/api";
import {
  DEFAULT_CLASS_FOR_CATEGORY,
  type ClassOptionsValue,
  type DocumentClass,
} from "@/types/documentClass";
import { useDocumentStamp } from "@/hooks/useDocumentStamp";
import { resolveCategory, resolveOptions, type StampDocumentInput } from "./stampText";
import { TypeAndClass } from "./sections/TypeAndClass";
import { ClassOptions } from "./sections/ClassOptions";
import { Layout } from "./sections/Layout";
import { Language } from "./sections/Language";
import { LookStampSection } from "./sections/Look";
import { isOwn } from "@/lib/documentAccess";
import { PublishWith } from "./sections/PublishWith";
import styles from "./DocumentStamp.module.scss";

export type DocumentStampStatus = "loading" | "ready";
export type DocumentStampPermission = "edit" | "readOnly";

export interface DocumentStampPopoverProps {
  document: Document;
  status: DocumentStampStatus;
  permission: DocumentStampPermission;
  /** Opened from the stamp's theme name: Look scrolled into view, its header focused. */
  focusLook?: boolean;
}

/**
 * The shell of the document-stamp popover. Owns the optimistic local
 * state for class options + category, wires changes through the
 * `useDocumentStamp` mutation, and renders the three states (loading,
 * save error, no permission) per the round-2 design spec.
 */
export function DocumentStampPopover({ document, status, permission, focusLook = false }: DocumentStampPopoverProps) {
  const isReadOnly = permission === "readOnly";

  const docInput = toStampInput(document);
  const initialCategory = resolveCategory(docInput);
  const initialOptions = resolveOptions(docInput);
  const initialClass = (document as unknown as { latexDocumentClass?: DocumentClass }).latexDocumentClass
    ?? DEFAULT_CLASS_FOR_CATEGORY[initialCategory];

  // Local optimistic state — mirrors the doc, applies optimistic updates,
  // reverts on error per the round-2 spec ("Failed value reverts to last
  // good. Never leave the user staring at a value the server rejected.").
  const [category, setCategory] = useState(initialCategory);
  const [options, setOptions] = useState<ClassOptionsValue>(initialOptions);
  const [currentClass, setCurrentClass] = useState<DocumentClass>(initialClass);

  // Re-sync local state when the underlying document changes (e.g. after
  // a successful save). We compare by value so spurious renders don't
  // clobber in-flight optimistic edits.
  useEffect(() => {
    setCategory(initialCategory);
    setOptions(initialOptions);
    setCurrentClass(initialClass);
    // We deliberately depend on the doc id + a few key fields rather than
    // the resolved values to keep the dependency stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    document.id,
    document.paperSize,
    document.fontSize,
    document.columns,
    (document as unknown as { latexDocumentClass?: string }).latexDocumentClass,
  ]);

  const stamp = useDocumentStamp(document.id);

  async function handleCategoryChange(next: typeof category) {
    if (isReadOnly) return;
    const prevCategory = category;
    const prevClass = currentClass;
    setCategory(next);
    setCurrentClass(DEFAULT_CLASS_FOR_CATEGORY[next]);
    try {
      await stamp.updateCategory(next);
    } catch {
      // Roll back optimistic update on save failure.
      setCategory(prevCategory);
      setCurrentClass(prevClass);
    }
  }

  async function handleOptionsChange(next: ClassOptionsValue) {
    if (isReadOnly) return;
    const prev = options;
    setOptions(next);
    try {
      await stamp.updateOptions(next);
    } catch {
      setOptions(prev);
    }
  }

  // Auto-dismiss the error toast after 4s, per the round-2 design.
  useEffect(() => {
    if (!stamp.saveError) return;
    const id = window.setTimeout(() => stamp.clearError(), 4000);
    return () => window.clearTimeout(id);
  }, [stamp.saveError, stamp.clearError]);

  if (status === "loading") {
    return <PopoverLoadingState />;
  }

  return (
    <div className={styles.fadeIn}>
      {stamp.saveError && (
        <button
          type="button"
          className={styles.errorToast}
          onClick={() => stamp.clearError()}
          data-testid="document-stamp-error-toast"
        >
          {i18n.t("editor:stamp.couldnTSaveRetry")}
        </button>
      )}

      <TypeAndClass
        category={category}
        currentClass={currentClass}
        disabled={isReadOnly}
        onChangeCategory={handleCategoryChange}
      />

      {/* Publisher templates (25 Oct 2026): a venue sets the class, its options and the layout. */}
      <PublishWith
        value={document.publisherProfile}
        disabled={isReadOnly}
        ownerOnly={!isReadOnly && !isOwn(document.role)}
        onChange={(publisherProfile) => void stamp.update({ publisherProfile })}
      />
      <ClassOptions
        value={options}
        onChange={handleOptionsChange}
        disabled={isReadOnly || !!document.publisherProfile}
      />

      <Language
        value={document.language}
        detected={document.detectedLanguage}
        disabled={isReadOnly}
        onChange={(language) => void stamp.update({ language })}
      />

      <LookStampSection
        document={document}
        disabled={isReadOnly}
        onSave={(look) => stamp.update({ look })}
        focusOnOpen={focusLook}
      />

      <Layout disabled={isReadOnly || !!document.publisherProfile} />

      {isReadOnly && (
        <p className={styles.permissionFooter}>{i18n.t("editor:stamp.askTheOwnerForEdit")}</p>
      )}
    </div>
  );
}

/** Render the popover skeletons matching the round-2 design. */
function PopoverLoadingState() {
  return (
    <div data-testid="document-stamp-loading">
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{i18n.t("editor:stamp.typeAmpClass")}</span>
        </div>
        <div className={styles.skelGrid}>
          <div className={`${styles.skel} ${styles.skelTile}`} />
          <div className={`${styles.skel} ${styles.skelTile}`} />
          <div className={`${styles.skel} ${styles.skelTile}`} />
          <div className={`${styles.skel} ${styles.skelTile}`} />
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{i18n.t("editor:stamp.classOptions")}</span>
        </div>
        <div className={styles.skelStack}>
          <div className={`${styles.skel} ${styles.skelPill}`} />
          <div className={`${styles.skel} ${styles.skelPill}`} />
          <div className={`${styles.skel} ${styles.skelPill}`} />
        </div>
      </div>
    </div>
  );
}

function toStampInput(doc: Document): StampDocumentInput {
  const loose = doc as unknown as StampDocumentInput;
  return {
    latexDocumentClass: loose.latexDocumentClass,
    documentCategory: loose.documentCategory,
    paperSize: doc.paperSize,
    fontSize: doc.fontSize,
    columns: doc.columns,
    sides: loose.sides,
    titlePage: loose.titlePage,
    orientation: loose.orientation,
  };
}
